/**
 * Keeps the hidden `selling_plan` input in step with the selected Laufzeit variant.
 *
 * Horizon morphs only the variant picker after a variant change on a product page
 * (assets/variant-picker.js:82), so anything outside it — including the input rendered
 * by snippets/variant-selling-plan.liquid — has to update itself.
 *
 * Two independent paths, on purpose:
 *   1. the hidden input, which the accelerated checkout button submits natively
 *   2. a `fetch` guard that derives the plan from the variant id in the request body,
 *      so an AJAX add-to-cart is correct even if the event above never fired
 */

// 'shopify:product:select' — see assets/standard-events.d.ts:650. Used as a literal so
// this module keeps working if the CDN events module fails to load.
const PRODUCT_SELECT_EVENT = 'shopify:product:select';

/** @type {Map<string, number | null>} */
const planByVariant = new Map();

/** @param {Element} form */
function setUpForm(form) {
  const mapElement = form.querySelector('[data-ef-selling-plan-map]');
  const input = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-ef-selling-plan]'));
  if (!mapElement?.textContent || !input) return;

  /** @type {Record<string, number | null>} */
  const map = JSON.parse(mapElement.textContent);
  for (const [variantId, planId] of Object.entries(map)) {
    planByVariant.set(String(variantId), planId);
  }

  /** @param {string | number} variantId */
  const apply = (variantId) => {
    const planId = map[String(variantId)];
    if (planId) {
      input.value = String(planId);
      input.disabled = false;
    } else {
      // A disabled input is not submitted — exactly what the 3-year one-time term needs.
      input.value = '';
      input.disabled = true;
    }
  };

  const scope = form.closest('.shopify-section') ?? document;

  scope.addEventListener(PRODUCT_SELECT_EVENT, (event) => {
    const promise = /** @type {{ promise?: Promise<any> }} */ (event).promise;
    if (!promise) return;

    promise
      .then((resolved) => {
        const variantId = resolved?.detail?.resource?.id;
        if (variantId) apply(variantId);
      })
      .catch(() => {});
  });

  // The page may have been opened with ?variant= pointing somewhere else than the default.
  const variantFromUrl = new URL(window.location.href).searchParams.get('variant');
  if (variantFromUrl) apply(variantFromUrl);
}

document.querySelectorAll('product-form-component').forEach(setUpForm);

if (planByVariant.size > 0) {
  const originalFetch = window.fetch.bind(window);

  window.fetch = function patchedFetch(input, init) {
    try {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.pathname : input?.url ?? '';
      const body = init?.body;

      if (url.split('?')[0]?.replace(/\.js$/, '').endsWith('/cart/add') && body instanceof FormData) {
        const variantId = body.get('id');

        if (variantId != null && planByVariant.has(String(variantId))) {
          const planId = planByVariant.get(String(variantId));
          if (planId) {
            body.set('selling_plan', String(planId));
          } else {
            body.delete('selling_plan');
          }
        }
      }
    } catch (error) {
      // Never let this break add-to-cart.
      console.warn('[EDGEFleet] Kaufoption konnte nicht aus der Variante abgeleitet werden:', error);
    }

    return originalFetch(input, init);
  };
}
